import { useState } from 'react'
import {
  LEAGUE_MOTTO_MAX_LENGTH,
  validateLeagueMotto,
} from '../../nameValidation'
import ModalBackdrop from '../ModalBackdrop'

interface MottoEditorModalProps {
  leagueName: string
  // The motto as currently set — seeds the draft and decides the title's wording.
  motto?: string | null
  onSave: (motto: string) => Promise<void> | void
  onClose: () => void
}

/**
 * Admin motto editor. Mounted only while open, so the draft, its validation error
 * and the in-flight flag live here rather than in the page: opening always starts from the motto as
 * currently set. Passing an empty string through `onSave` clears the motto.
 */
function MottoEditorModal({
  leagueName,
  motto,
  onSave,
  onClose,
}: MottoEditorModalProps) {
  const [draft, setDraft] = useState(motto ?? '')
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  const handleSave = async () => {
    const result = validateLeagueMotto(draft)
    if (!result.valid) {
      setError(result.error)
      return
    }
    setIsSaving(true)
    try {
      await onSave(draft)
      onClose()
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <ModalBackdrop onClose={onClose}>
      <div className="league-detail__motto-editor">
        {/* The eyebrow carries the league name so the modal says which league you're
            editing — the title is the invitation, so it can't double as the label. */}
        <p className="league-detail__motto-editor-label label-eyebrow">
          {leagueName}
        </p>
        <p className="league-detail__motto-editor-title">
          {motto ? 'Say something else' : 'Say something'}
        </p>
        <p className="league-detail__motto-editor-hint">
          One line under your league name. Change it whenever.
        </p>
        <div className="league-detail__motto-editor-field">
          <input
            className="league-detail__motto-editor-input"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value)
              if (error) setError(null)
            }}
            placeholder="e.g. Second place is just first loser"
            maxLength={LEAGUE_MOTTO_MAX_LENGTH}
            autoFocus
          />
          <span className="league-detail__motto-editor-count">
            {draft.length}/{LEAGUE_MOTTO_MAX_LENGTH}
          </span>
        </div>
        {error && (
          <p className="league-detail__motto-editor-error">{error}</p>
        )}
        <div className="league-detail__motto-editor-actions">
          <button
            type="button"
            className="league-detail__motto-editor-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className="league-detail__motto-editor-save"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </ModalBackdrop>
  )
}

export default MottoEditorModal
